/**
 * Widget font catalogue — the SINGLE source of truth for the per-widget "Font"
 * picker in the report editor's Appearance section.
 *
 * The same set is used in three places, so they can never drift:
 *  - the editor's Font dropdown (label + family),
 *  - the web canvas `@font-face` loader (so the browser renders the chosen font),
 *  - the server PDF font registration (react-pdf needs each font's TTF file —
 *    it has no system fonts), giving editor ≡ PDF parity.
 *
 * Files are served from the Fontsource jsDelivr CDN (stable, predictable paths,
 * ships .ttf which react-pdf's fontkit requires; WOFF2 is not supported there).
 * The `family` string is what gets stored in `config.style.fontFamily` and what
 * BOTH CSS `font-family` and `Font.register({ family })` reference.
 */

export interface WidgetFontOption {
  /** Stored in config.style.fontFamily; used by CSS + react-pdf Font.register. */
  family: string;
  /** Dropdown label. */
  label: string;
  /** Fontsource slug (for building the CDN URLs). */
  slug: string;
  /** Weights available on the CDN (defaults to [400, 700]). Display fonts like
   *  Bebas Neue ship 400 only. */
  weights?: (400 | 700)[];
}

/** Weights to load/register for a font (default 400 + 700). */
export function widgetFontWeights(font: WidgetFontOption): (400 | 700)[] {
  return font.weights ?? [400, 700];
}

const CDN = "https://cdn.jsdelivr.net/fontsource/fonts";

/** Font file URL for a slug + weight (400 normal / 700 normal). */
export function widgetFontUrl(slug: string, weight: 400 | 700): string {
  return `${CDN}/${slug}@latest/latin-${weight}-normal.ttf`;
}

/**
 * The picker set. Inter is the default (matches the rest of the app + the PDF's
 * existing body font). Each entry's TTF (400 + 700) is registered on the server
 * and loaded on the web via @font-face, so the rendered font matches everywhere.
 */
export const WIDGET_FONT_OPTIONS: WidgetFontOption[] = [
  { family: "Inter", label: "Inter (default)", slug: "inter" },
  { family: "Inter Tight", label: "Inter Tight", slug: "inter-tight" },
  { family: "Roboto", label: "Roboto", slug: "roboto" },
  { family: "Open Sans", label: "Open Sans", slug: "open-sans" },
  { family: "Source Sans 3", label: "Source Sans 3", slug: "source-sans-3" },
  { family: "Lato", label: "Lato", slug: "lato" },
  { family: "Work Sans", label: "Work Sans", slug: "work-sans" },
  { family: "Nunito", label: "Nunito", slug: "nunito" },
  { family: "Poppins", label: "Poppins", slug: "poppins" },
  { family: "Montserrat", label: "Montserrat", slug: "montserrat" },
  { family: "Raleway", label: "Raleway", slug: "raleway" },
  { family: "Merriweather", label: "Merriweather (serif)", slug: "merriweather" },
  { family: "Lora", label: "Lora (serif)", slug: "lora" },
  { family: "PT Serif", label: "PT Serif (serif)", slug: "pt-serif" },
  { family: "Playfair Display", label: "Playfair Display (serif)", slug: "playfair-display" },
  { family: "Bebas Neue", label: "Bebas Neue (display)", slug: "bebas-neue", weights: [400] },
  { family: "JetBrains Mono", label: "JetBrains Mono (mono)", slug: "jetbrains-mono" },
];

/** Default family (no explicit choice → the app's base font). */
export const DEFAULT_WIDGET_FONT = "Inter";
